import { Controller } from "@hotwired/stimulus"

/**
 * Filter Coordinator Controller
 * 
 * Coordinates filter form submissions with table sorting and pagination.
 * Works alongside sortable-table-controller to ensure URL state consistency.
 * 
 * Features:
 * - Intercepts form submissions to control URL building
 * - Preserves sort parameters when filtering
 * - Resets pagination when filters change
 * - Provides "clear filters" that optionally keeps sort
 * 
 * Usage:
 *   <div data-controller="filter-coordinator">
 *     <form data-filter-coordinator-target="form" 
 *           data-action="submit->filter-coordinator#submit">
 *       <!-- filter inputs -->
 *     </form>
 *     <button data-action="click->filter-coordinator#clear">Clear</button>
 *   </div>
 * 
 * Options:
 *   data-filter-coordinator-preserve-sort-value="true"  (default: true)
 *   data-filter-coordinator-base-url-value="/talents"   (default: current path)
 */
export default class extends Controller {
  static targets = ["form"]
  static values = {
    preserveSort: { type: Boolean, default: true },
    baseUrl: String
  }

  /**
   * Handle form submission
   */
  submit(event) {
    event.preventDefault()
    
    const form = event.target
    const formData = new FormData(form)
    const url = this.buildFilterUrl(formData)
    
    // Dispatch event for other controllers
    this.dispatch('filter', {
      detail: { formData, url: url.toString() }
    })
    
    window.location.href = url.toString()
  }

  /**
   * Build URL from form data with smart parameter preservation
   */
  buildFilterUrl(formData) {
    const baseUrl = this.baseUrlValue || window.location.pathname
    const url = new URL(baseUrl, window.location.origin)
    
    // Add all non-empty form fields
    for (const [key, value] of formData.entries()) {
      if (this.shouldIncludeParam(key, value)) {
        url.searchParams.append(key, value)
      }
    }
    
    // Preserve sort parameters if enabled
    if (this.preserveSortValue) {
      this.preserveSortParams(url)
    }
    
    // Always reset pagination when filters change
    url.searchParams.delete('page')
    
    return url
  }

  /**
   * Determine if a parameter should be included in URL
   */
  shouldIncludeParam(key, value) {
    // Skip empty values
    if (value === '' || value === null || value === undefined) {
      return false
    }
    
    // Skip Rails authenticity token
    if (key === 'authenticity_token') {
      return false
    }
    
    // Skip format parameter (handled by Rails)
    if (key === 'format') {
      return false
    }
    
    return true
  }

  /**
   * Preserve sort parameters from current URL
   */
  preserveSortParams(url) {
    const currentParams = new URLSearchParams(window.location.search)
    
    if (currentParams.has('sort')) {
      url.searchParams.set('sort', currentParams.get('sort'))
      
      if (currentParams.has('direction')) {
        url.searchParams.set('direction', currentParams.get('direction'))
      }
    }
  }

  /**
   * Clear all filters
   */
  clear(event) {
    event?.preventDefault()
    
    const baseUrl = this.baseUrlValue || window.location.pathname
    const url = new URL(baseUrl, window.location.origin)
    
    // Optionally preserve sort
    if (this.preserveSortValue) {
      this.preserveSortParams(url)
    }
    
    // Dispatch event
    this.dispatch('clear', {
      detail: { url: url.toString() }
    })
    
    window.location.href = url.toString()
  }

  /**
   * Public API: Get current filter state as object
   */
  getCurrentFilters() {
    const params = new URLSearchParams(window.location.search)
    const filters = {}
    
    for (const [key, value] of params.entries()) {
      // Skip pagination and sort params
      if (key === 'page' || key === 'sort' || key === 'direction') {
        continue
      }
      filters[key] = value
    }
    
    return filters
  }

  /**
   * Public API: Check if any filters are active
   */
  hasActiveFilters() {
    return Object.keys(this.getCurrentFilters()).length > 0
  }
}
